import React, { useState, useEffect } from 'react';
import {
    Button,
    Spin,
    Card,
    Form,
    Input,
    Select,
    Switch,
    Breadcrumb,
    Row,
    Col,
    Typography,
    Tag,
    App,
} from 'antd';
import {
    UserOutlined,
    MailOutlined,
    TeamOutlined,
    IdcardOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../services/api';
import { usePageTitle } from '../context/PageTitleContext';
import ErrorPage from './ErrorPage/ErrorPage';

const { Title, Text } = Typography;
const { Option } = Select;

const UserEdit = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    const [form] = Form.useForm();
    const { message } = App.useApp();

    const [userData, setUserData] = useState(null);
    const [externalRoles, setExternalRoles] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Группировка ролей по системам
    const groupedRoles = externalRoles.reduce((acc, role) => {
        if (!acc[role.system]) {
            acc[role.system] = [];
        }
        acc[role.system].push(role);
        return acc;
    }, {});

    useEffect(() => {
        // Загрузка списка внешних ролей
        const fetchExternalRoles = async () => {
            try {
                const rolesResponse = await userService.getExternalRoles();
                setExternalRoles(rolesResponse.data || []);
                const warehousesResponse = await userService.getWarehousesList();
                setWarehouses(warehousesResponse.data || []);
            } catch (error) {
                message.error('Не удалось загрузить список ролей');
            }
        };

        fetchExternalRoles();
    }, []);

    useEffect(() => {
        setPageTitle('Редактирование пользователя');

        setRightContent(
            <Button
                type="primary"
                size="large"
                loading={submitting}
                onClick={() => form.submit()}
            >
                Сохранить
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent, submitting, form]);

    useEffect(() => {
        fetchUserData();
    }, [id]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const userData = await userService.getUserProfile(id);

            // Здесь данные тоже уже извлечены в сервисе
            setUserData(userData);
            form.setFieldsValue({
                username: userData.username,
                email: userData.email,
                first_name: userData.first_name,
                last_name: userData.last_name,
                department: userData.department,
                position: userData.position,
                role_id: userData.role === 'admin' ? 1 : 3, // Преобразуем роль в role_id
                is_active: userData.is_active,
                warehouse_ids: userData.warehouse_ids || []
            });
        } catch (error) {
            console.error('Error fetching user details:', error);

            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Пользователь не найден",
                    description: "Указанный пользователь не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных пользователя. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);
            const externalRoleFields = Object.keys(values)
                .filter(key => key.startsWith('role_'))
                .reduce((obj, key) => {
                    obj[key] = values[key];
                    return obj;
                }, {});

            const updateData = {
                ...values,
                ...externalRoleFields
            };

            const result = await userService.updateUserProfile(id, updateData);

            const responseData = result.data ?? result;

            if (result.success) {
                message.success(responseData.message || 'Данные пользователя успешно обновлены');
                navigate(`/users/${id}`);
            } else {
                message.error(responseData?.message || 'Не удалось обновить данные пользователя');
            }
        } catch (error) {
            console.error('Error updating user:', error);
            message.error(error.response?.data?.message || 'Ошибка при обновлении данных пользователя');
        } finally {
            setSubmitting(false);
        }
    };


    const goBack = () => {
        navigate(`/users/${id}`);
    };

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <>
            <Breadcrumb
                items={[
                    { title: <a onClick={() => navigate('/users')}>Пользователи</a> },
                    { title: <a onClick={goBack}>{userData?.username || `Пользователь #${id}`}</a> },
                    { title: 'Редактирование' }
                ]}
                style={{ marginBottom: 16 }}
            />

            <Card
                style={{ marginBottom: 24 }}
            >
                <div style={{display: 'flex', alignItems: 'flex-start'}}>
                    <div style={{marginRight: 24}}>
                        <div
                            style={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: '#1677ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 36,
                                color: 'white'
                            }}
                        >
                            {getInitials(userData?.first_name, userData?.last_name)}
                        </div>
                    </div>

                    <div>
                        <Title level={3} style={{marginTop: 0, marginBottom: 8}}>
                            {userData?.first_name} {userData?.last_name}
                        </Title>
                        <Text type="secondary">{userData?.username}</Text>
                        <div style={{marginTop: 8}}>
                            <Tag color={userData?.is_active ? 'green' : 'red'}>
                                {userData?.is_active ? 'Активен' : 'Неактивен'}
                            </Tag>
                            <Tag color={userData?.role === 'admin' ? 'blue' : 'default'}>
                                {userData?.role === 'admin' ? 'Администратор' : 'Пользователь'}
                            </Tag>
                        </div>
                    </div>
                </div>
            </Card>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    is_active: true,
                    role_id: 3 // Пользователь по умолчанию
                }}
                size="large"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Card
                            title={
                                    'Учетные данные'
                            }
                            style={{marginBottom: 16}}
                        >
                        <Form.Item
                                name="username"
                                label="Логин"
                                rules={[
                                    { required: true, message: 'Пожалуйста, введите логин' },
                                    { min: 3, message: 'Логин должен содержать минимум 3 символа' }
                                ]}
                            >
                                <Input prefix={<UserOutlined />} placeholder="Введите логин" />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    { required: true, message: 'Пожалуйста, введите email' },
                                    { type: 'email', message: 'Пожалуйста, введите корректный email' }
                                ]}
                            >
                                <Input prefix={<MailOutlined />} placeholder="Введите email" />
                            </Form.Item>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card
                            title="Настройки доступа"
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="role_id"
                                label="Роль"
                                rules={[{ required: true, message: 'Пожалуйста, выберите роль' }]}
                            >
                                <Select placeholder="Выберите роль">
                                    <Option value={1}>Администратор</Option>
                                    <Option value={3}>Пользователь</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="is_active"
                                label="Активен"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
                            </Form.Item>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card
                            title={'Личные данные'
                            }
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="first_name"
                                label="Имя"
                            >
                                <Input placeholder="Введите имя" />
                            </Form.Item>

                            <Form.Item
                                name="last_name"
                                label="Фамилия"
                            >
                                <Input placeholder="Введите фамилию" />
                            </Form.Item>

                            <Form.Item
                                name="department"
                                label="Отдел"
                            >
                                <Input prefix={<TeamOutlined />} placeholder="Введите отдел" />
                            </Form.Item>

                            <Form.Item
                                name="position"
                                label="Должность"
                            >
                                <Input prefix={<IdcardOutlined />} placeholder="Введите должность" />
                            </Form.Item>
                        </Card>
                    </Col>


                    <Col span={12}>
                        <Card
                            title={'Роли во внешних системах'
                            }
                            style={{marginBottom: 16}}
                        >
                            {Object.entries(groupedRoles).map(([system, roles]) => (
                                <div key={system}>
                                    <Form.Item
                                        name={`role_${system}`}
                                        label={`Роль в системе ${system.toUpperCase()}`}
                                        initialValue={userData?.[`role_${system}`] || undefined}
                                    >
                                        <Select
                                            showSearch
                                            placeholder={`Выберите роль для ${system}`}
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            allowClear
                                        >
                                            {roles.map(role => (
                                                <Option
                                                    key={role.id}
                                                    value={role.id}
                                                    style={
                                                        role.id === userData?.[`role_${system}`]
                                                            ? { fontWeight: 'bold', backgroundColor: '#f0f0f0' }
                                                            : {}
                                                    }
                                                >
                                                    {role.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </div>
                            ))}
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card
                            title="Доступ к складам в WMS"
                            style={{ marginBottom: 16 }}
                        >
                            <Form.Item
                                name="warehouse_ids"
                                label="Склады"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Выберите склады"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    allowClear
                                >
                                    {warehouses.map(warehouse => (
                                        <Option key={warehouse.id} value={warehouse.id}>
                                            {warehouse.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </>
    );
};

// Вспомогательная функция для получения инициалов пользователя
const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return 'U';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
};

export default UserEdit;